"use client";

import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { doc, getDoc, query, collection, where, limit, getDocs } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { FileText, Calendar, Shield, ArrowLeft, Download, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import BackButton from "@/components/ui/back-button";
import { downloadFile } from "@/lib/download";
import { toast } from "sonner";
import { getResourceBySlug } from "@/lib/resources";

export default function PyqDetailPage() {
  const { slug: id } = useParams();
  const [pyq, setPyq] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPyq = async () => {
      if (!id) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const data = await getResourceBySlug("pyq", id);
        
        if (data) {
          if (data.isVisible === false) {
            setPyq(null);
          } else {
            setPyq(data);
          }
        } else {
          setPyq(null);
        }
      } catch (error) {
        console.error("Error fetching PYQ archive:", error);
        setPyq(null);
      } finally {
        setLoading(false);
      }
    };
    fetchPyq();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-[#b48c36]/20 border-t-[#b48c36] rounded-full animate-spin" />
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.4em]">Retrieving Archive...</p>
        </div>
      </div>
    );
  }

  if (!pyq) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-8">
        <div className="text-center space-y-6">
          <h2 className="text-3xl font-serif font-bold text-slate-950">Archive Not Found</h2>
          <p className="text-slate-500 max-w-md mx-auto">The requested examination portal could not be located in our institutional database.</p>
          <Link to="/">
            <Button className="bg-slate-950 text-white hover:bg-[#b48c36] transition-colors px-8 py-6 rounded-none font-bold uppercase tracking-widest text-[10px]">
              Return to Repository
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const isTSPSC = pyq.exam?.includes("TSPSC") || pyq.title?.toLowerCase().includes("tspc");
  const isAPPSC = pyq.exam?.includes("APPSC") || pyq.title?.toLowerCase().includes("appc");

  return (
    <div className="min-h-screen bg-white">
      {/* Header / Breadcrumb */}
      <div className="bg-slate-50 border-b border-slate-100 py-6 sm:py-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <BackButton className="mb-6" />
          
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div className="space-y-4 max-w-3xl">
              <div className="flex items-center gap-3">
                <span className={cn(
                  "px-3 py-1 rounded-sm text-[9px] font-bold uppercase tracking-[0.2em] text-white",
                  isTSPSC ? "bg-blue-600 shadow-[0_0_15px_rgba(37,99,235,0.3)]" : 
                  isAPPSC ? "bg-emerald-700 shadow-[0_0_15px_rgba(4,120,87,0.3)]" : 
                  "bg-[#b48c36]"
                )}>
                  {pyq.exam || "Examination Portal"}
                </span>
                <div className="w-1.5 h-1.5 rounded-full bg-slate-200" />
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
                  <Calendar size={14} /> Year: {pyq.year || "Official Release"}
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-slate-950 leading-[1.1] tracking-tight">
                {pyq.title}
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12 sm:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
          
          {/* Main Description */}
          <div className="lg:col-span-8 space-y-10">
            <div className="flex flex-col gap-6">
               <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-slate-950 text-[#b48c36] flex items-center justify-center rounded-none shadow-xl transform -rotate-3 hover:rotate-0 transition-transform">
                     <FileText size={20} />
                  </div>
                  <h2 className="text-[11px] font-black text-slate-950 uppercase tracking-[0.4em]">Institutional Context & Syllabus</h2>
               </div>
               
               <div className="prose prose-slate prose-lg max-w-none">
                  <div className="text-slate-600 leading-[1.8] font-sans font-light whitespace-pre-wrap text-lg sm:text-xl">
                    {pyq.description || "The following examination archive is curated by Rohith's Academy HIAS editorial team for institutional aspirants. This paper represents the standard quality maintained by the respective commission for that session."}
                  </div>
               </div>
            </div>

            {/* Premium Divider */}
            <div className="py-8 flex items-center gap-6">
               <div className="flex-1 h-px bg-slate-100" />
               <Shield size={20} className="text-[#b48c36] opacity-30" />
               <div className="flex-1 h-px bg-slate-100" />
            </div>

            {/* Document Download Zone */}
            <div className="space-y-8" id="download">
               <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-serif font-bold text-slate-950">Official Documentation</h3>
                    <p className="text-[10px] text-slate-400 uppercase tracking-widest font-bold mt-1">Available papers for this examination</p>
                  </div>
               </div>

                <div className="grid grid-cols-1 gap-4">
                  {(pyq.documentUrls || []).map((url: string, idx: number) => (
                    <motion.div 
                      key={idx}
                      onClick={async () => {
                        const toastId = toast.loading("Preparing download...");
                        const success = await downloadFile(url, `${pyq.title || "QuestionPaper"}_${idx+1}.pdf`);
                        if (success) toast.success("Download started", { id: toastId });
                        else toast.error("Download failed, opening in tab", { id: toastId });
                      }}
                      whileHover={{ scale: 1.01, x: 8 }}
                      className="group block bg-white border border-slate-100 hover:border-[#b48c36]/50 p-6 flex items-center gap-6 transition-all shadow-sm hover:shadow-xl no-underline cursor-pointer"
                    >
                      <div className="w-14 h-14 bg-slate-50 flex items-center justify-center text-slate-300 group-hover:bg-[#b48c36]/10 group-hover:text-[#b48c36] transition-colors relative overflow-hidden">
                         <div className="absolute inset-0 border border-slate-100/50" />
                         <FileText size={28} />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                         <h4 className="text-sm font-bold text-slate-950 uppercase tracking-widest mb-1 group-hover:text-[#b48c36] transition-colors">
                           Examination Paper {idx + 1}
                         </h4>
                         <div className="flex items-center gap-4 text-[10px] text-slate-400 font-medium uppercase tracking-widest">
                            <span className="flex items-center gap-1.5"><Download size={12} /> PDF Download</span>
                            <div className="w-1 h-1 rounded-full bg-slate-300" />
                            <span>Encrypted Server</span>
                         </div>
                      </div>

                      <div className="rounded-none bg-slate-950 text-white group-hover:bg-[#b48c36] px-6 h-10 text-[9px] font-bold uppercase tracking-widest transition-all flex items-center justify-center">
                        Get Document <ArrowLeft className="ml-2 rotate-180" size={12} />
                      </div>
                    </motion.div>
                  ))}

                  {(pyq.documentUrls || []).length === 0 && (
                    <div className="p-16 border-2 border-dashed border-slate-100 text-center rounded-3xl">
                       <FileText size={48} className="mx-auto text-slate-200 mb-4" />
                       <p className="text-slate-400 font-serif italic">Digital archive is currently being updated...</p>
                    </div>
                  )}
               </div>
            </div>
          </div>

          {/* Sidebar / Stats */}
          <div className="lg:col-span-4 sticky top-32 space-y-8">
            <div className="bg-slate-950 p-8 text-white relative overflow-hidden">
              {/* Gold Accent */}
              <div className="absolute top-0 right-0 w-20 h-20 bg-[#b48c36]/10 blur-3xl rounded-full" />
              
              <h3 className="text-xs font-black text-[#b48c36] uppercase tracking-[0.4em] mb-6">Paper Metadata</h3>
              
              <div className="space-y-6">
                <div>
                  <label className="text-[10px] text-slate-500 uppercase tracking-widest block mb-2 font-bold">Document Title</label>
                  <p className="text-sm font-serif font-medium">{pyq.title}</p>
                </div>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="text-[10px] text-slate-500 uppercase tracking-widest block mb-2 font-bold">Portal</label>
                    <p className="text-sm font-serif font-medium">{pyq.exam || "Official"}</p>
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-500 uppercase tracking-widest block mb-2 font-bold">Year</label>
                    <p className="text-sm font-serif font-medium">{pyq.year || "Archived"}</p>
                  </div>
                </div>
                
                <div className="pt-6 border-t border-white/10">
                    <Button 
                     className="w-full bg-[#b48c36] hover:bg-white hover:text-slate-950 text-white font-bold text-[10px] uppercase tracking-widest h-12 transition-all group"
                     onClick={async () => {
                        const urls = pyq.documentUrls || [];
                        if (urls.length === 1) {
                          const toastId = toast.loading("Downloading...");
                          await downloadFile(urls[0], `${pyq.title}.pdf`);
                          toast.success("Download started", { id: toastId });
                        } else {
                          document.getElementById('download')?.scrollIntoView({ behavior: 'smooth' });
                        }
                     }}
                    >
                      {pyq.documentUrls?.length === 1 ? "Get Question Paper" : "Download Files"} <Download size={14} className="ml-2 group-hover:translate-y-0.5 transition-transform" />
                    </Button>
                 </div>
              </div>
            </div>

            <div className="p-8 border border-slate-100 bg-slate-50/50">
               <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mb-4">Study Notice</h3>
               <p className="text-xs text-slate-500 leading-relaxed italic font-serif">
                 "Previous Year Questions are the ultimate guide to understanding the examiner's mindset. Analyze every paper twice before solving new mock tests."
               </p>
               <div className="mt-4 flex items-center gap-3">
                  <div className="w-6 h-px bg-[#b48c36]" />
                  <span className="text-[9px] font-bold uppercase tracking-widest text-[#b48c36]">Rohith's Academy HIAS</span>
               </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
